// BiscuitTiny — leaderboard-row mini pet, sized for inline use (24–32px).
//
// Ported from remix-of-activity-tracker. The teammate/leaderboard data plumbing
// (Supabase pet_state_public, useTeamPets pre-fetch, useTeamMoods, look variants)
// has been stripped. Pure visual + mood, rendered with the chibi corgi PNGs.

import type { BiscuitMood } from '@/lib/biscuit/mood'

interface BiscuitTinyProps {
  /** Mood to render. Defaults to "content". */
  mood?: BiscuitMood
  /** Pixel size of the avatar (default 28). */
  size?: number
  /** Optional click handler — when provided, renders as a button. */
  onClick?: () => void
  /** Accepted for API symmetry with BiscuitFull/Mini — Tiny has no internal label to gate. */
  showLabel?: boolean
}

export function BiscuitTiny({ mood = 'content', size = 28, onClick }: BiscuitTinyProps) {
  // Biscuit is decorative in 99% of uses (alongside a labeled header, inside a
  // labeled button). Marking it aria-hidden keeps screen readers from announcing
  // "Biscuit looking happy" next to every screen title.
  //
  // 256px AVIF/WebP variants are ~10kB / ~5kB vs the source PNG at ~1.2MB.
  // Lossy compression is invisible at the 28–56px sizes this component renders.
  const inner = (
    <picture>
      <source srcSet={`/biscuit/${mood}-256.avif`} type="image/avif" />
      <source srcSet={`/biscuit/${mood}-256.webp`} type="image/webp" />
      <img
        src={`/biscuit/${mood}.png`}
        alt=""
        aria-hidden="true"
        className="w-8 h-8 object-contain"
        style={{ width: size, height: size }}
        loading="lazy"
        decoding="async"
      />
    </picture>
  )

  if (onClick) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onClick()
        }}
        aria-label={`Biscuit (${mood})`}
        className="rounded-full bg-[hsl(var(--biscuit-cream))] shrink-0 flex items-center justify-center hover:ring-2 hover:ring-primary/40 active:scale-95 motion-reduce:active:scale-100 transition motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary overflow-hidden"
        style={{ width: size, height: size }}
        title={`Biscuit (${mood})`}
      >
        {inner}
      </button>
    )
  }

  return (
    <div
      className="rounded-full bg-[hsl(var(--biscuit-cream))] shrink-0 flex items-center justify-center overflow-hidden motion-safe:hover:animate-biscuit-tilt cursor-default"
      style={{ width: size, height: size }}
      title={`Biscuit (${mood})`}
      aria-hidden="true"
    >
      {inner}
    </div>
  )
}
